import { Chip, Grid, Typography } from '@mui/material'
import {
  CheckCircleOutlineIcon,
  CloseIcon,
  HomeIcon,
  PhoneIcon,
  PhoneIphoneIcon,
  TranslateIcon,
  WorkOutlineIcon,
} from 'components/Icons'
import PreviewCard from 'components/PreviewCard'
import { useSession } from 'components/Providers'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import FieldSkeleton from './FieldSkeleton'
import 'utils/global.scss'

const phoneIcon = phone =>
  phone?.type === 'home' ? (
    <HomeIcon />
  ) : phone?.type === 'mobile' ? (
    <PhoneIphoneIcon />
  ) : phone?.type === 'work' ? (
    <WorkOutlineIcon />
  ) : (
    <PhoneIcon />
  )

// The guardian record carries SIS/parental access, so it remains staff-managed. A parent can
// only open "More" on their own card to change their own contact information.
export default function GuardianCard({ data, title, languageName, onMore }) {
  const { t } = useTranslation()
  const theme = useSelector(state => state.session.appearance)
  const { person } = useSession()
  const isOwn = Boolean(data?.contact?.id) && String(data.contact.id) === String(person?.id)

  const Field = ({ label, children }) => (
    <Grid item sm={4} xs={4} md={4}>
      <Typography className={`grid-table-title-${theme}`} variant="body2" sx={{ fontSize: '12px' }}>
        {label}
      </Typography>
      <Typography variant="body1" className="grid-table-title-value">
        {children}
      </Typography>
    </Grid>
  )

  const relationship = data?.relationship
  const fullName = [data?.contact?.title, data?.contact?.firstName, data?.contact?.lastName]
    .filter(Boolean)
    .join(' ')

  return (
    <PreviewCard
      title={title}
      size="large"
      otherButton={isOwn && onMore ? { buttonLabel: t('More'), buttonClick: onMore } : undefined}
      actionChildren={
        data?.id && (
          <>
            <Chip
              icon={data.emergency ? <CheckCircleOutlineIcon /> : <CloseIcon />}
              label={t('Emergency Call')}
              color={data.emergency ? 'success' : 'error'}
            />
            <Chip
              icon={data.pickup ? <CheckCircleOutlineIcon /> : <CloseIcon />}
              label={t('Pickup')}
              color={data.pickup ? 'success' : 'error'}
            />
            {data.isGuardian && (
              <Chip
                icon={data.hasParentalAccess ? <CheckCircleOutlineIcon /> : <CloseIcon />}
                label={t('SIS Access')}
                color={data.hasParentalAccess ? 'success' : 'error'}
              />
            )}
          </>
        )
      }
    >
      <Grid container>
        <Field label={t('Relationship')}>
          {relationship ? (
            <Typography>
              {relationship.slice(0, 1).toUpperCase()}
              {relationship.slice(1)}
            </Typography>
          ) : (
            <FieldSkeleton />
          )}
        </Field>

        <Field label={t('Full Name')}>
          {fullName ? <Typography>{fullName}</Typography> : <FieldSkeleton />}
        </Field>

        <Field label={t('Email')}>
          {data?.contact?.email ? <Typography>{data.contact.email}</Typography> : <FieldSkeleton />}
        </Field>
        <Field label={t('Username')}>
          {data?.contact?.user?.username ? <Typography>{data.contact.user.username}</Typography> : <FieldSkeleton />}
        </Field>

        <Field label={t('Primary Phone')}>
          {data?.contact?.phones?.[0] ? (
            <Chip icon={phoneIcon(data.contact.phones[0])} label={data.contact.phones[0]?.number} />
          ) : (
            <FieldSkeleton />
          )}
        </Field>

        <Field label={t('Alternate Phone')}>
          {data?.contact?.phones?.[1] ? (
            <Chip icon={phoneIcon(data.contact.phones[1])} label={data.contact.phones[1]?.number} />
          ) : (
            <FieldSkeleton />
          )}
        </Field>

        <Field label={t('Preferred Language')}>
          {languageName ? <Chip icon={<TranslateIcon />} label={languageName} /> : <FieldSkeleton />}
        </Field>
      </Grid>
    </PreviewCard>
  )
}
